import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@offline-mui/material'
import { useEffect, useState } from 'react'
import { updateBooking } from '../api/inventory'
import type { BookingPassenger, InventoryBooking, PassengerDetails } from '../api/inventory'

type PassengerFormRow = {
  id: number
  name: string
  airlineRecordLocator: string
  ticketNumber: string
}

type EditBookingDialogProps = {
  open: boolean
  booking: InventoryBooking | null
  onClose: () => void
  onSaved: (booking: InventoryBooking) => void
}

function passengerName(details: PassengerDetails | null | undefined): string {
  if (!details) return 'Passenger'
  return (
    [details.prefix, details.firstName, details.middleName, details.lastName]
      .filter(Boolean)
      .join(' ')
      .trim() || 'Passenger'
  )
}

function toFormRows(passengers: BookingPassenger[] | null | undefined): PassengerFormRow[] {
  return (passengers || []).map((passenger) => ({
    id: passenger.id,
    name: passengerName(passenger.pax_details),
    airlineRecordLocator: passenger.airline_record_locator || '',
    ticketNumber: passenger.ticket_number || '',
  }))
}

export default function EditBookingDialog({
  open,
  booking,
  onClose,
  onSaved,
}: EditBookingDialogProps) {
  const [rows, setRows] = useState<PassengerFormRow[]>([])
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setFormError(null)
    setRows(toFormRows(booking?.passengers))
  }, [open, booking])

  const updateRow = (id: number, field: 'airlineRecordLocator' | 'ticketNumber', value: string) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    )
  }

  const handleSave = async () => {
    if (!booking) return
    setSaving(true)
    setFormError(null)
    try {
      const result = await updateBooking(booking.booking_id, {
        passengers: rows.map((row) => ({
          id: row.id,
          airlineRecordLocator: row.airlineRecordLocator.trim() || null,
          ticketNumber: row.ticketNumber.trim() || null,
        })),
      })
      onSaved(result.data)
      onClose()
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {booking ? `Edit booking ${booking.booking_id}` : 'Edit booking'}
      </DialogTitle>
      <DialogContent dividers>
        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}
        {rows.length ? (
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {rows.map((row) => (
              <Stack
                key={row.id}
                spacing={1.5}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1.5,
                }}
              >
                <Typography sx={{ fontWeight: 700 }}>{row.name}</Typography>
                <Grid container spacing={1.5}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="PNR"
                      value={row.airlineRecordLocator}
                      onChange={(event) =>
                        updateRow(row.id, 'airlineRecordLocator', event.target.value)
                      }
                      placeholder="ABC123"
                      helperText="Airline record locator"
                      slotProps={{ htmlInput: { maxLength: 32 } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="E-ticket number"
                      value={row.ticketNumber}
                      onChange={(event) =>
                        updateRow(row.id, 'ticketNumber', event.target.value)
                      }
                      placeholder="0012345678901"
                      slotProps={{ htmlInput: { maxLength: 32 } }}
                    />
                  </Grid>
                </Grid>
              </Stack>
            ))}
          </Stack>
        ) : (
          <Alert severity="info">This booking has no passenger records to edit.</Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!rows.length || saving}
          onClick={() => void handleSave()}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Save changes
        </Button>
      </DialogActions>
    </Dialog>
  )
}
